// ══════════════════════════════════════════════════════════
// DOMAIN TYPES
//
// Estados y contratos para dominios personalizados.
// El frontend NUNCA modifica DNS ni activa SSL.
//
// Estados del spec:
// PENDING → DNS_REQUIRED → VERIFYING → VERIFIED → ACTIVE
// También: FAILED, SUSPENDED, REMOVED
// ══════════════════════════════════════════════════════════

// ── ESTADOS ──

export type DomainStatus =
  | 'PENDING'
  | 'DNS_REQUIRED'
  | 'VERIFYING'
  | 'VERIFIED'
  | 'ACTIVE'
  | 'FAILED'
  | 'SUSPENDED'
  | 'REMOVED';

export type SslStatus =
  | 'NOT_REQUESTED'
  | 'REQUESTING'
  | 'ACTIVE'
  | 'EXPIRED'
  | 'ERROR';

// ── REGISTROS DNS ──

/** Registro DNS entregado por el backend autorizado. */
export interface DnsRecord {
  type: 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX' | 'NS';
  host: string;
  value: string;
  ttl: number;
  /** Solo cuando aplica (ej: MX) */
  priority?: number;
}

// ── RESPUESTAS DEL BACKEND ──

export interface CustomDomainRequest {
  domain: string;
  radio_id: string;
}

export interface CustomDomainResponse {
  id: string;
  radio_id: string;
  domain: string;
  status: DomainStatus;
  dns_records: DnsRecord[];
  verified_at: string | null;
  activated_at: string | null;
  ssl_status: SslStatus;
  ssl_expires_at: string | null;
  error_message: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface VerificationHistoryEntry {
  id: string;
  timestamp: string;
  result: 'success' | 'failure';
  message: string;
}

export interface VerifyDomainResponse {
  verified: boolean;
  status: DomainStatus;
  message: string;
  records_checked: number;
  records_passing: number;
}

// ── INTERFAZ PARA LA UI ──

export interface DomainUIData {
  domainId: string | null;
  domain: string | null;
  status: DomainStatus;
  sslStatus: SslStatus;
  sslExpiresAt: string | null;
  dnsRecords: DnsRecord[];
  verifiedAt: string | null;
  activatedAt: string | null;
  errorMessage: string | null;
  isPrimary: boolean;
  verificationHistory: VerificationHistoryEntry[];
}

// ── CONSTANTES DE ESTADOS ──

export const DOMAIN_STATUS_LABELS: Record<DomainStatus, { label: string; icon: string; colorClass: string; bgClass: string }> = {
  PENDING:       { label: 'Pendiente', icon: 'ri-time-line', colorClass: 'text-amber-600', bgClass: 'bg-amber-50' },
  DNS_REQUIRED:  { label: 'DNS Requerido', icon: 'ri-settings-3-line', colorClass: 'text-amber-600', bgClass: 'bg-amber-50' },
  VERIFYING:     { label: 'Verificando', icon: 'ri-loader-4-line', colorClass: 'text-blue-600', bgClass: 'bg-blue-50' },
  VERIFIED:      { label: 'Verificado', icon: 'ri-check-double-line', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50' },
  ACTIVE:        { label: 'Activo', icon: 'ri-check-line', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50' },
  FAILED:        { label: 'Falló', icon: 'ri-error-warning-line', colorClass: 'text-red-500', bgClass: 'bg-red-50' },
  SUSPENDED:     { label: 'Suspendido', icon: 'ri-pause-circle-line', colorClass: 'text-amber-600', bgClass: 'bg-amber-50' },
  REMOVED:       { label: 'Eliminado', icon: 'ri-delete-bin-line', colorClass: 'text-foreground-400', bgClass: 'bg-background-100' },
};

export const SSL_STATUS_LABELS: Record<SslStatus, { label: string; icon: string; colorClass: string; bgClass: string }> = {
  NOT_REQUESTED: { label: 'No solicitado', icon: 'ri-shield-line', colorClass: 'text-foreground-400', bgClass: 'bg-background-100' },
  REQUESTING:    { label: 'Solicitando', icon: 'ri-loader-4-line', colorClass: 'text-blue-600', bgClass: 'bg-blue-50' },
  ACTIVE:        { label: 'Activo', icon: 'ri-shield-check-line', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50' },
  EXPIRED:       { label: 'Expirado', icon: 'ri-shield-cross-line', colorClass: 'text-red-500', bgClass: 'bg-red-50' },
  ERROR:         { label: 'Error SSL', icon: 'ri-error-warning-line', colorClass: 'text-red-500', bgClass: 'bg-red-50' },
};

// ── VALIDACIÓN DE DOMINIO ──

export interface DomainValidationResult {
  valid: boolean;
  sanitized: string | null;
  error: string | null;
}

const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const IPV4_REGEX = /^(\d{1,3}\.){3}\d{1,3}$/;
const RESERVED_DOMAINS = [
  'localhost',
  'example.com',
  'test.com',
  'radiofrecuenciaweb.com',
  'rfw.local',
];

export function validateDomain(rawInput: string): DomainValidationResult {
  if (!rawInput || !rawInput.trim()) {
    return { valid: false, sanitized: null, error: 'El dominio no puede estar vacío.' };
  }

  let domain = rawInput.trim().toLowerCase();

  // Quitar protocolo
  domain = domain.replace(/^https?:\/\//, '');

  // Quitar ruta
  domain = domain.replace(/\/.*$/, '');

  // Quitar query string
  domain = domain.replace(/\?.*$/, '');

  // Quitar puerto
  domain = domain.replace(/:\d+$/, '');

  // Quitar trailing slash y espacios
  domain = domain.replace(/\/+$/, '').trim();

  // Quitar www. inicial
  domain = domain.replace(/^www\./, '');

  if (!domain) {
    return { valid: false, sanitized: null, error: 'El dominio está vacío después de normalizar.' };
  }

  // Rechazar wildcard
  if (domain.includes('*')) {
    return { valid: false, sanitized: null, error: 'No se permiten wildcards (*).' };
  }

  // Rechazar localhost
  if (domain === 'localhost' || domain.startsWith('localhost.')) {
    return { valid: false, sanitized: null, error: 'localhost no es un dominio válido para este servicio.' };
  }

  // Rechazar IP
  if (IPV4_REGEX.test(domain) || domain.includes('::')) {
    return { valid: false, sanitized: null, error: 'No se permiten direcciones IP. Ingresá un nombre de dominio.' };
  }

  // Rechazar dominios reservados
  if (RESERVED_DOMAINS.includes(domain)) {
    return { valid: false, sanitized: null, error: 'Este dominio está reservado y no puede usarse.' };
  }

  // Validar formato general
  if (!DOMAIN_REGEX.test(domain)) {
    return { valid: false, sanitized: null, error: 'El formato del dominio no es válido. Ejemplo: miradio.com' };
  }

  // Longitud máxima
  if (domain.length > 253) {
    return { valid: false, sanitized: null, error: 'El dominio excede la longitud máxima permitida (253 caracteres).' };
  }

  return { valid: true, sanitized: domain, error: null };
}

// ── ESTADO VACÍO ──

export function buildEmptyDomainUIData(): DomainUIData {
  return {
    domainId: null,
    domain: null,
    status: 'PENDING',
    sslStatus: 'NOT_REQUESTED',
    sslExpiresAt: null,
    dnsRecords: [],
    verifiedAt: null,
    activatedAt: null,
    errorMessage: null,
    isPrimary: false,
    verificationHistory: [],
  };
}